import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import Skeleton from '@mui/material/Skeleton';
import { Chip, ChipProps } from '../Chip';
export interface ChipListItem {
  /**
   * a stable identifier for this chip, passed back to onRemove
   */
  id: string;
  /**
   * the chip's visible label
   */
  label: string;
  /**
   * optional, defaults to false. While true, a skeleton placeholder is rendered in place of this chip (not
   * removable) - use for a chip whose real label isn't resolved yet, rather than showing a raw id
   */
  isLoading?: boolean;
}
/** matches Chip's own medium-size height so a skeleton sits at the same height as a resolved chip */
const SkeletonChip = ({ testId }: { testId: string }): JSX.Element => (
  <Skeleton
    data-testid={testId}
    variant="rounded"
    animation="wave"
    width={72}
    height={26}
    sx={{ borderRadius: '13px' }}
  />
);
export interface ChipListProps {
  /**
   * the chips to render, in order
   */
  chips: ChipListItem[];
  /**
   * called with a chip's id when its remove ('x') is clicked, whether it's currently shown inline or inside
   * the overflow popover
   */
  onRemove: (id: string) => void;
  /**
   * the colour applied to every chip, reusing Chip's own colour enum
   * @default 'blue'
   */
  color?: ChipProps['color'];
  /**
   * optional. When the number of chips exceeds this, only the first `maxVisible` are shown inline and the
   * rest collapse behind a trailing "+N" chip that opens a popover listing them. When omitted, every chip is
   * shown inline with no overflow behaviour.
   */
  maxVisible?: number;
  /**
   * the data-testid of the root element
   * @default 'chip-list'
   */
  'data-testid'?: string;
}
/**
 * Renders a row of removable chips, collapsing any beyond `maxVisible` behind a trailing "+N" chip that opens
 * a popover listing the rest - also removable from there.
 */
export const ChipList: React.FC<ChipListProps> = ({
  chips,
  onRemove,
  color = 'blue',
  maxVisible,
  'data-testid': dataTestId = 'chip-list',
}: ChipListProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const hasOverflow = typeof maxVisible === 'number' && chips.length > maxVisible;
  const visibleChips = hasOverflow ? chips.slice(0, maxVisible) : chips;
  const overflowChips = hasOverflow ? chips.slice(maxVisible) : [];
  // if the overflow chips are removed down to (or below) maxVisible while the popover is open, close it
  // rather than leave it anchored to a chip that's no longer rendered
  useEffect(() => {
    if (!hasOverflow) setAnchorEl(null);
  }, [hasOverflow]);
  if (chips.length === 0) return null;
  return (
    <Box data-testid={dataTestId} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
      {visibleChips.map((chip) =>
        chip.isLoading ? (
          <SkeletonChip key={chip.id} testId={`chip-${chip.id}`} />
        ) : (
          <Chip
            key={chip.id}
            label={chip.label}
            color={color}
            data-testid={`chip-${chip.id}`}
            endAdornment={{ mode: 'delete', onDelete: () => onRemove(chip.id) }}
          />
        ),
      )}
      {hasOverflow && (
        <>
          <Chip
            label={`+${overflowChips.length}`}
            color={color}
            clickable
            onClick={(event) => setAnchorEl(event.currentTarget)}
            data-testid="chip-list-overflow-trigger"
          />
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{ paper: { sx: { mt: 1 } } }}
          >
            <Box
              data-testid="chip-list-overflow-popover"
              sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, p: 3, maxWidth: '320px' }}
            >
              {overflowChips.map((chip) =>
                chip.isLoading ? (
                  <SkeletonChip key={chip.id} testId={`chip-${chip.id}`} />
                ) : (
                  <Chip
                    key={chip.id}
                    label={chip.label}
                    color={color}
                    data-testid={`chip-${chip.id}`}
                    endAdornment={{ mode: 'delete', onDelete: () => onRemove(chip.id) }}
                  />
                ),
              )}
            </Box>
          </Popover>
        </>
      )}
    </Box>
  );
};
